import { stringifyUnknownValue } from "@/utils/stringifyUnknownValue"

import type { ChartDataPoint, ChartWidgetConfig } from "../../types/chart"
import { GROUP_BY_TYPE, type GroupByType } from "../../types/chart-types"
import { CANONICAL_STRING_ORDER, getYesNoUnknownOrder } from "./colorSchemes"

export const UNICODE_SEPARATOR = "\u001E"

/**
 * Data key for the single series an ungrouped chart draws. It is a layout sentinel, not a value the
 * aggregate produced, so a click on it must not be reported as a string category.
 */
export const UNGROUPED_SERIES_KEY = "value"
const METADATA_GROUP_KEY_PREFIX = "__metadata_group__:"
const METADATA_REMAINDER_LABEL = "Other (remaining)"

interface MetadataGroupIdentity {
  kind: "value" | "remainder"
  metadataKey?: string
  topValues?: string[]
  value: string | null
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === "string")
}

export function hasChartGrouping(
  config: Pick<ChartWidgetConfig, "groupBy" | "groupByMetadataKey" | "splitBy">,
): boolean {
  return Boolean(config.groupBy || config.splitBy || config.groupByMetadataKey)
}

export function createMetadataGroupKey({
  kind,
  metadataKey,
  topValues,
  value,
}: MetadataGroupIdentity): string {
  return `${METADATA_GROUP_KEY_PREFIX}${JSON.stringify([
    kind,
    value,
    kind === "remainder" ? (topValues ?? null) : null,
    metadataKey ?? null,
  ])}`
}

export function parseMetadataGroupKey(
  key: string,
): MetadataGroupIdentity | null {
  if (!key.startsWith(METADATA_GROUP_KEY_PREFIX)) {
    return null
  }

  try {
    const parsed: unknown = JSON.parse(
      key.slice(METADATA_GROUP_KEY_PREFIX.length),
    )
    if (!Array.isArray(parsed)) {
      return null
    }

    const kind: unknown = parsed[0]
    const value: unknown = parsed[1]
    const topValues: unknown = parsed[2]
    const metadataKey: unknown = parsed[3] ?? null
    if (
      (kind !== "value" && kind !== "remainder") ||
      (value !== null && typeof value !== "string") ||
      (topValues !== null && !isStringArray(topValues)) ||
      (metadataKey !== null && typeof metadataKey !== "string")
    ) {
      return null
    }

    return {
      kind,
      ...(metadataKey ? { metadataKey } : {}),
      value,
      topValues: kind === "remainder" ? (topValues ?? undefined) : undefined,
    }
  } catch {
    return null
  }
}

export function getMetadataGroupDisplayName(key: string): string {
  const identity = parseMetadataGroupKey(key)
  if (!identity) {
    return key
  }
  if (identity.kind === "remainder") {
    return METADATA_REMAINDER_LABEL
  }
  if (identity.value === null) {
    return "Unknown"
  }
  return identity.value === METADATA_REMAINDER_LABEL
    ? `${identity.value} (value)`
    : identity.value
}

/** Display order for YES, NO, UNKNOWN (Format Options, legend, tooltip) */
const YES_NO_UNKNOWN_DISPLAY_ORDER: Record<string, number> = {
  YES: 0,
  NO: 1,
  UNKNOWN: 2,
  Yes: 0,
  No: 1,
  Unknown: 2,
}

/**
 * Extract string value from a key for sorting (handles split keys with UNICODE_SEPARATOR).
 */
export function getStringValueForSort(key: string): string {
  return key.includes(UNICODE_SEPARATOR)
    ? parseSplitKey(key).stringValue
    : getMetadataGroupDisplayName(key)
}

/**
 * Extract string value from a Recharts payload entry (legend/tooltip).
 */
export function getStringValueFromPayload(entry: {
  dataKey?: unknown
  value?: unknown
  name?: unknown
}): string {
  const dataKey = typeof entry.dataKey === "string" ? entry.dataKey : ""
  return dataKey.includes(UNICODE_SEPARATOR)
    ? parseSplitKey(dataKey).stringValue
    : dataKey || stringifyUnknownValue(entry.value ?? entry.name ?? "")
}

/**
 * Sort by YES, NO, UNKNOWN display order, optional fallback order, then alphabetically.
 */
export function sortByYesNoUnknown<T>(
  items: T[],
  getStringValue: (item: T) => string,
  fallbackOrder?: Record<string, number>,
  resolveDisplayName?: (raw: string) => string,
): T[] {
  return [...items].sort((a, b) => {
    const rawA = getStringValue(a)
    const rawB = getStringValue(b)
    const orderA =
      YES_NO_UNKNOWN_DISPLAY_ORDER[rawA] ?? fallbackOrder?.[rawA] ?? 999
    const orderB =
      YES_NO_UNKNOWN_DISPLAY_ORDER[rawB] ?? fallbackOrder?.[rawB] ?? 999
    if (orderA !== orderB) {
      return orderA - orderB
    }
    const valA = resolveDisplayName ? resolveDisplayName(rawA) : rawA
    const valB = resolveDisplayName ? resolveDisplayName(rawB) : rawB
    return valA.localeCompare(valB, undefined, {
      numeric: true,
      sensitivity: "base",
    })
  })
}

/**
 * Extract the string value from a split key (handles Unicode separator for grouped keys)
 */
export const extractStringValue = (key: string): string => {
  return key.includes(UNICODE_SEPARATOR)
    ? key.split(UNICODE_SEPARATOR).slice(1).join(UNICODE_SEPARATOR)
    : getMetadataGroupDisplayName(key)
}

/**
 * Parse a combined key into its group ID and string value components.
 * For keys with Unicode separator (e.g., "agentId␞YES"), returns both parts.
 * For simple keys, returns the key as stringValue with undefined groupId.
 */
export const parseSplitKey = (
  key: string,
): { groupId: string | undefined; stringValue: string } => {
  if (!key.includes(UNICODE_SEPARATOR)) {
    return { groupId: undefined, stringValue: key }
  }

  const parts = key.split(UNICODE_SEPARATOR)
  return {
    groupId: parts[0],
    stringValue: parts.slice(1).join(UNICODE_SEPARATOR),
  }
}

/**
 * Calculate split keys for multi-series rendering with consistent ordering.
 * Ensures visual continuity by maintaining the same vertical order across all stacks.
 * When targetValues is provided and includes "NO", uses no-yes-unknown stacking; else yes-no-unknown.
 */
export const calculateSplitKeys = (
  chartData: readonly ChartDataPoint[],
  config: ChartWidgetConfig,
  targetValues?: string[],
): string[] => {
  // First, always check what keys actually exist in the data
  const keys = new Set<string>()
  chartData.forEach((item) => {
    Object.keys(item).forEach((key) => {
      if (
        key !== "date" &&
        key !== "timestamp" &&
        key !== "bucketTime" &&
        key !== "value"
      ) {
        keys.add(key)
      }
    })
  })

  if (
    !config.splitBy &&
    !config.groupBy &&
    !config.groupByMetadataKey &&
    config.metricOutputType !== "string" &&
    keys.size === 0
  ) {
    return []
  }

  // Sort keys for consistent ordering across all stacks
  const yesNoOrder = getYesNoUnknownOrder(targetValues)
  const stringOrder = { ...CANONICAL_STRING_ORDER, ...yesNoOrder }

  return Array.from(keys).sort((a, b) => {
    const stringValueA = extractStringValue(a)
    const stringValueB = extractStringValue(b)

    // Priority 1: Use canonical ordering if both values are in the map
    const orderA = stringOrder[stringValueA]
    const orderB = stringOrder[stringValueB]

    if (orderA !== undefined && orderB !== undefined) {
      return orderA - orderB
    }

    // Priority 2: Canonical values come before non-canonical
    if (orderA !== undefined) {
      return -1
    }
    if (orderB !== undefined) {
      return 1
    }

    // Priority 3: Alphabetical for non-canonical values
    return stringValueA.localeCompare(stringValueB)
  })
}

/**
 * Get the split key value from an item based on the groupBy configuration.
 * Returns the specific field value or "Unknown" if not found.
 * Does not fall back to other fields to avoid incorrect grouping.
 */
export const getSplitKeyFromItem = (
  groupBy: GroupByType | undefined,
  item: {
    run_agent_id?: string | null
    mutation_id?: string | null
    persona_id?: string | null
    run_template_name?: string | null
    test_set_id?: string | null
    assignee?: string | null
    metadata_value?: string | null
    metadata_group_kind?: "value" | "remainder" | null
    metadata_top_values?: string[] | null
  },
  groupByMetadataKey?: string,
): string => {
  if (groupByMetadataKey) {
    return createMetadataGroupKey({
      kind: item.metadata_group_kind ?? "value",
      metadataKey: groupByMetadataKey,
      topValues: item.metadata_top_values ?? undefined,
      value: item.metadata_value ?? null,
    })
  }

  if (!groupBy) {
    return "Unknown"
  }

  switch (groupBy) {
    case GROUP_BY_TYPE.AGENT:
      return item.run_agent_id || "Unknown"
    case GROUP_BY_TYPE.MUTATION:
      return item.mutation_id || "Unknown"
    case GROUP_BY_TYPE.PERSONA:
      return item.persona_id || "Unknown"
    case GROUP_BY_TYPE.TEMPLATE:
      return item.run_template_name || "Unknown"
    case GROUP_BY_TYPE.TEST_SET:
      return item.test_set_id || "Unknown"
    case GROUP_BY_TYPE.ASSIGNEE:
      return item.assignee || "Unknown"
    default:
      return "Unknown"
  }
}
